import { useTheme } from '@mui/material/styles';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import InputField from './InputField';
import Typography from '@mui/material/Typography';

interface EditableFieldProps {
  value: string;
  onChange: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  width?: number;
  suffix?: string;
  suffixColor?: string;
  suffixFontSize?: string;
  readOnly?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  validateChange?: (_nextValue: string) => boolean;
  type?: 'text' | 'number' | 'password' | 'email';
  inputProps?: object;

  /** Simplified commit */
  onCommit?: (_value: string) => void;

  disabled?: boolean;
  disabledTooltip?: string;
}

const EditableField: React.FC<EditableFieldProps> = React.memo(
  ({
    value: _value,
    onChange,
    placeholder,
    width = '100%',
    suffix,
    suffixColor,
    suffixFontSize = '15px',
    readOnly = false,
    onBlur,
    onFocus,
    validateChange,
    type = 'text',
    inputProps,
    onCommit,
    disabled = false,
    disabledTooltip,
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [internalValue, setInternalValue] = useState(_value);
    const inputRef = useRef<HTMLInputElement>(null);

    // Keep internal value synced with parent
    useEffect(() => {
      setInternalValue(_value);
    }, [_value]);

    const stopEventPropagation = (e: any) => {
      e.stopPropagation();
    };

    const handleInternalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      if (validateChange && !validateChange(next)) return;

      setInternalValue(next);
      onChange(event); // immediate passthrough
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      stopEventPropagation(e);
      onFocus?.();
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      stopEventPropagation(e);

      // Always commit onBlur (normal input behavior)
      onCommit?.(internalValue);

      onBlur?.();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      stopEventPropagation(event);

      if (event.key === 'Escape') {
        setInternalValue(_value); // revert
        inputRef.current?.blur();
        return;
      }

      // For number type inputs, restrict allowed keys
      if (type === 'number') {
        const allowedKeys = [
          // Navigation keys
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
          'Home',
          'End',
          'Tab',
          // Editing keys
          'Backspace',
          'Delete',
          // Special keys
          'Enter',
          'Escape',
          // Decimal point
          '.',
          // Minus sign for negative numbers
          '-',
        ];

        const isNumber = /^[0-9]$/.test(event.key);
        const isAllowedKey = allowedKeys.includes(event.key);
        const isCtrlCommand = event.ctrlKey || event.metaKey;
        const isSelectAllCopyPaste =
          isCtrlCommand && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase());

        if (!isNumber && !isAllowedKey && !isSelectAllCopyPaste) {
          event.preventDefault();
        }
      }
    };

    // Prevent scroll from changing number input value
    const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
      if (type === 'number') {
        // Blur the input to prevent scroll from changing value
        (event.target as HTMLInputElement).blur();
        event.preventDefault();
      }
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      stopEventPropagation(e);
      e.preventDefault();

      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select?.();
      }, 0);
    };

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
          cursor: readOnly ? 'default' : 'pointer',
          borderRadius: `${theme.borderRadius.sm}px`,
          transition: `background-color ${theme.transitions.custom.fast}`,
          height: '100%',
        }}
        onMouseDown={handleMouseDown}
      >
        <InputField
          ref={inputRef}
          value={internalValue}
          onChange={handleInternalChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onWheel={handleWheel}
          onClick={(e: any) => stopEventPropagation(e)}
          onMouseDown={(e: any) => stopEventPropagation(e)}
          placeholder={placeholder}
          readOnly={readOnly}
          type={type}
          inputProps={inputProps}
          disabled={disabled}
          fullWidth
          sx={{
            width,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            '& .MuiInputBase-root': {
              minHeight: '30px !important',
              height: '30px !important',
              transition: `all ${theme.transitions.custom.fast}`,
            },
            '& .MuiInputBase-input': {
              cursor: readOnly ? 'default' : 'text',
              '&::placeholder': {
                color: theme.palette.borderLight,
              },
            },
          }}
          endAdornment={
            suffix && (
              <Typography
                sx={{
                  fontSize: suffixFontSize,
                  color: suffixColor ?? theme.palette.text.primary,
                  opacity: 0.9,
                  transition: `opacity ${theme.transitions.custom.fast}`,
                  textTransform: ['usd', 'eur'].includes(suffix) ? 'uppercase' : '',
                }}
              >
                {suffix}
              </Typography>
            )
          }
        />

        {disabled && (
          <Tooltip title={disabledTooltip || t('common.sumpTankDisabledTooltip')}>
            <InfoOutlineIcon
              sx={{
                color: theme.palette.icons.warning,
                fontSize: '18px',
                marginLeft: '4px',
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  },
);

EditableField.displayName = 'EditableField';

export default EditableField;
